import { ClockIcon, ExclamationTriangleIcon, LockClosedIcon, LockOpenIcon } from "@heroicons/react/20/solid";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import { useMemo } from "react";

import Button from "~/components/buttons/Button/Button";
import Tooltip from "~/components/Tooltip";
import usePageStateStorage from "~/hooks/usePageStateStorage";
import type {
	MonthlyClosingCloseRequest,
	MonthlyClosingRead,
	MonthlyClosingReopenRequest,
} from "~/modules/timeTracking/api/monthlyClosing/monthlyClosingTypes.ts";
import { ClosingListTable } from "~/modules/timeTracking/components/closingLists/ClosingListTable.tsx";
import type { ClosingProjectGroup } from "~/modules/timeTracking/components/closingLists/closingListTypes.ts";
import { nonProjectTitle } from "~/modules/timeTracking/components/closingLists/closingListTypes.ts";

type Props = {
	closing: MonthlyClosingRead;
	onCloseRequest: (request: MonthlyClosingCloseRequest) => void;
	onReopenRequest: (request: MonthlyClosingReopenRequest) => void;
};

export function MonthlyClosingPanel(props: Props) {
	// One stored state for the whole page: the choice holds for every month and every user.
	const { pageState, setPageState } = usePageStateStorage({
		pageKey: "timeTrackingMonthlyClosingPanel",
		defaultState: { expanded: false },
	});

	// The list comes as the backend sends it, with no filter by activeStaffings: it can hold an
	// inactive staffing that has trackings in the month, and that staffing needs its closing too.
	const groups = useMemo(() => {
		const result: ClosingProjectGroup[] = [];
		for (const staffing of props.closing.staffings) {
			let group = result.find((group) => group.projectId === staffing.projectId);
			if (!group) {
				group = { projectId: staffing.projectId, projectName: staffing.projectName, companyAvatarImage: staffing.companyAvatarImage, staffings: [] };
				result.push(group);
			}
			group.staffings.push(staffing);
		}
		return result;
	}, [props.closing.staffings]);

	// A temporarily unlocked staffing counts as open. The non-project part counts as one more entry next to the staffings.
	const openLines = props.closing.staffings
		.filter((staffing) => !staffing.isClosed || staffing.unlocks.length > 0)
		.map((staffing) => `${staffing.orderName} (${staffing.projectName})`);
	if (!props.closing.nonProject.isClosed) openLines.push(nonProjectTitle);
	const totalCount = props.closing.staffings.length + 1;
	const closedCount = totalCount - openLines.length;
	const expanded = pageState.expanded;
	// A month that was never closed shows no warning: its open parts are no exception to a closing.
	const hasClosingExceptions = props.closing.hasMonthClosing && !props.closing.isMonthClosed && openLines.length > 0;
	// An unlock keeps the month closed, so it needs its own warning next to the exceptions.
	const hasUnlocks = props.closing.staffings.some((staffing) => staffing.unlocks.length > 0);

	return (
		<div className="mb-10 overflow-hidden rounded-2xl bg-white">
			<div className="flex items-center gap-3 px-4 py-3">
				<span className="size-5 shrink-0 text-primary-500"
					  title={props.closing.isMonthClosed ? "Monat geschlossen" : "Monat offen"}>
					{props.closing.isMonthClosed ? <LockClosedIcon className="size-full" /> :
						<LockOpenIcon className="size-full" />}
				</span>
				{props.closing.isMonthClosed ? (
					<span className="text-sm font-semibold text-gray-800">Monat geschlossen</span>
				) : hasClosingExceptions ? null : (
					// A closed month with exceptions closes each exception in the list: "all" would clear them together.
					<Button theme="primary"
							size="sm"
							rounded="md"
							onClick={() => props.onCloseRequest({ scope: { scope: "all" }, label: "den ganzen Monat" })}>
						Monat schließen
					</Button>
				)}
				<div className="ml-auto flex items-center gap-3 text-xs text-gray-600">
					<span>{closedCount} von {totalCount} geschlossen</span>
					{(hasClosingExceptions || hasUnlocks) && (
						<Tooltip text={`Noch offen:\n${openLines.join("\n")}`}
								 className="whitespace-pre-line">
							<span className="-ml-1 flex items-center gap-1">
								<ExclamationTriangleIcon className="size-4 translate-y-px text-amber-500" />
								{hasUnlocks && <ClockIcon className="size-4 text-amber-700" />}
							</span>
						</Tooltip>
					)}
					<Button theme="white"
							size="sm"
							rounded="md"
							className="w-32 border border-gray-300"
							aria-expanded={expanded}
							onClick={() => setPageState({ expanded: !expanded })}>
						{expanded ? "Alle verbergen" : "Alle anzeigen"}
						<ChevronDownIcon className={clsx("size-3.5 transition-transform", expanded && "rotate-180")} />
					</Button>
				</div>
			</div>
			{expanded && (
				<div className="border-t border-gray-200 px-4 py-3">
					<ClosingListTable groups={groups}
									  nonProject={props.closing.nonProject}
									  onCloseRequest={props.onCloseRequest}
									  onReopenRequest={props.onReopenRequest} />
				</div>
			)}
		</div>
	);
}
